import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

/**
 * Per-user relevance signal on a single job (Spec 045).
 * SAVED  = positive signal, surfaced in the Saved tab.
 * HIDDEN = "not relevant", excluded from the default feed, surfaced in the Hidden
 *          tab, and accompanied by an immutable analysis snapshot.
 */
export const jobInteractionKindSchema = z.enum(["SAVED", "HIDDEN"]);
export type JobInteractionKind = z.infer<typeof jobInteractionKindSchema>;

export const upsertJobInteractionSchema = z.object({
  kind: jobInteractionKindSchema,
  /** Active job profile providing the matching criteria for the hide snapshot. */
  jobProfileId: z.string().trim().min(1).optional(),
});

export class UpsertJobInteractionDto extends createZodDto(upsertJobInteractionSchema) {}

/**
 * Immutable snapshot captured when a job is hidden, so the negative signal can be
 * analysed later even after the profile/resume/job change. Shape mirrors what is
 * available at hide time; fields are nullable when the job was not yet scored.
 */
export const hideSnapshotSchema = z.object({
  capturedAt: z.string(),
  job: z.object({
    id: z.string(),
    title: z.string(),
    company: z.string(),
    board: z.string(),
    location: z.string().nullable().default(null),
    url: z.string(),
  }),
  criteria: z.object({
    jobProfileId: z.string().nullable().default(null),
    mainSkills: z.array(z.string()).default([]),
    optionalSkills: z.array(z.string()).default([]),
    targetTitles: z.array(z.string()).default([]),
    targetLocations: z.array(z.string()).default([]),
  }),
  assessment: z
    .object({
      score: z.number().nullable().default(null),
      reason: z.string().nullable().default(null),
      matchedKeywords: z.array(z.string()).default([]),
      missingKeywords: z.array(z.string()).default([]),
    })
    .nullable()
    .default(null),
});

export type HideSnapshot = z.infer<typeof hideSnapshotSchema>;

export const jobInteractionSchema = z.object({
  id: z.string(),
  jobId: z.string(),
  kind: jobInteractionKindSchema,
  jobProfileId: z.string().nullable().default(null),
  snapshot: hideSnapshotSchema.nullable().default(null),
  createdAt: z.date(),
});

export type JobInteractionDto = z.infer<typeof jobInteractionSchema>;
